import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  IconButton,
} from "@mui/material";
import { DeleteOutline, EditOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import DeleteAlert from "../Components/AlertBox/DeleteAlert";
import { useDispatch, useSelector } from "react-redux";
import ApiAlert from "../Components/AlertBox/ApiAlert";
import { useDebounce } from "../hooks/useDebounce";
import { getQuestionsData, editQuestionData } from "../Redux/Questions/action";
import { columns2 } from "../constants/masterQuestion";
import { IOSSwitchButton } from "../Components/ReusableComponents/SwitchButtons";
import { roleModulePermissions } from "../utils/roleInstance";
import SearchFeild from "../Components/Table/SearchFeild";

const QuestionTable = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [switchInactive, setSwitchInactive] = useState(null);
  const [alert, setAlert] = useState(false);
  const [alertContent, setalertcontent] = useState("");
  const [alertStatus, setAlertStatus] = useState(true);
  const [open, setOpen] = React.useState(false);
  const { debouncedValue } = useDebounce(search, 500);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const masterQuestions = useSelector(
    (state) => state.questionReducer.questions
  );
  const masterQuestionsLength = useSelector(
    (state) => state.questionReducer.length
  );

  const [questionEditAction, setQuestionEditAction] = useState(false);
  const [questionDeleteAction, setQuestionDeleteAction] = useState(false);

  // --- All your logic functions remain unchanged ---
  const handleClickOpen = (data) => {
    setOpen(true);
    setSwitchInactive(data);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const getQuestionsDataFn = () => {
    dispatch(getQuestionsData(page + 1, rowsPerPage, debouncedValue));
  };

  useEffect(() => {
    // Reset to first page on new search
    setPage(0);
  }, [debouncedValue]);

  useEffect(() => {
    getQuestionsDataFn();
    const alert_content = JSON.parse(localStorage.getItem("questionToast"));
    if (alert_content?.alertContent) {
      setAlert(true);
      setalertcontent(alert_content.alertContent);
      setAlertStatus(alert_content.alertStatus);
      setTimeout(() => {
        setAlert(false);
        localStorage.removeItem("questionToast");
      }, 2000);
    }
  }, [page, rowsPerPage, debouncedValue]);

  const AlerthandleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setAlert(false);
  };

  const updateMasterQuestionStatus = (data) => {
    const newStatus = !data.questionStatus;
    dispatch(editQuestionData(data.qId, { questionStatus: newStatus })).then(
      (res) => {
        if (res.type === `EDIT_QUESTIONS_SUCCESS`) {
          handleClose();
          getQuestionsDataFn();
        } else {
          console.error("edit master question err: ", res);
          getQuestionsDataFn();
        }
      }
    );
  };

  useEffect(() => {
    const checkQuestionAction = async () => {
      const roleData = { roleModule: "Questions" };
      const [editPermission, deletePermission] = await Promise.all([
        roleModulePermissions.checkUserRoles(roleData, "edit"),
        roleModulePermissions.checkUserRoles(roleData, "delete"),
      ]);
      setQuestionEditAction(editPermission);
      setQuestionDeleteAction(deletePermission);
    };
    checkQuestionAction();
  }, [page]);

  return (
    <>
      {alert && (
        <ApiAlert
          open={alert}
          snackbaronClose={AlerthandleClose}
          alertType={alertStatus ? "success" : "error"}
          Content={alertContent}
        />
      )}
      {/* ✅ This is the main flex container for the whole page */}
      <Box
        sx={{
          width: "100%",
          height: "calc(100vh - 170px)", // Adjust this value to match your header height
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <SearchFeild
            onChange={(e) => setSearch(e.target.value)}
            buttonName="Add Master Question"
            Navigate={() => navigate("add_question")}
            placeholder="Search Question or Question Type"
            roleModule={"Questions"}
          />
        </Box>

        {/* ✅ This Paper container now correctly grows to fill available space */}
        <Paper
          sx={{
            width: "100%",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <TableContainer sx={{ flexGrow: 1, overflow: "auto" }}>
            <Table sx={{ minWidth: 1200 }}>
              <TableHead sx={{ position: "sticky", top: 0, zIndex: 2 }}>
                <TableRow>
                  {columns2.map((column) => (
                    <TableCell
                      key={column.id}
                      align="center"
                      sx={{
                        minWidth: column.minWidth,
                        backgroundColor: "primary.main",
                        color: "secondary.main",
                        fontWeight: 600,
                        ...(column.id === "Action" && {
                          position: "",
                          right: 0,
                          zIndex: 3,
                          // borderLeft: '1px solid',
                          borderColor: "divider",
                        }),
                      }}
                    >
                      {column.id === "Action" &&
                      !(questionEditAction || questionDeleteAction)
                        ? ""
                        : column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {masterQuestions?.map((item, index) => (
                  <TableRow
                    key={item.qId || index}
                    sx={{
                      cursor: item.questionStatus ? "pointer" : "not-allowed",
                      opacity: item.questionStatus ? 1 : 0.6, // makes disabled items look dimmed
                      backgroundColor: (theme) =>
                        item.questionStatus
                          ? theme.palette.background.paper // normal
                          : theme.palette.action.disabledBackground, // subtle grey for disabled
                      "&:hover": {
                        backgroundColor: (theme) =>
                          item.questionStatus
                            ? theme.palette.action.hover
                            : theme.palette.action.disabledBackground, // prevent hover color on disabled
                      },
                      transition: "background-color 0.2s ease",
                    }}
                  >
                    <TableCell align="center">
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell align="left">
                      {item?.Question || "N/A"}
                    </TableCell>
                    <TableCell align="left">
                      {item?.questionType || "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      {item?.Weight || "N/A"}
                    </TableCell>
                    <TableCell align="center">{item?.Ack || "N/A"}</TableCell>
                    <TableCell align="center">
                      {item?.defaultValue || "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      {item?.["Next Question Prefix"] || "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      {item?.["Help Text"] || "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      {item.expectDocument === "yes"
                        ? "Yes"
                        : item.expectDocument === "no"
                        ? "No"
                        : "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="center"
                      >
                        {questionEditAction && item?.questionStatus && (
                          <Tooltip title="Edit Question" arrow>
                            <IconButton
                              size="small"
                              sx={{
                                "&:hover": {
                                  color: "rgb(254, 154, 37)",
                                  cursor: "pointer",
                                },
                              }}
                              onClick={() =>
                                navigate(`edit_question/${item.qId}`)
                              }
                            >
                              <EditOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {questionDeleteAction && (
                          <Tooltip
                            title={
                              item.questionStatus ? "Deactivate" : "Activate"
                            }
                            arrow
                          >
                            <Box sx={{ transform: "scale(0.7)" }}>
                              <IOSSwitchButton
                                checked={item.questionStatus}
                                onChange={() =>
                                  item.questionStatus
                                    ? handleClickOpen(item)
                                    : updateMasterQuestionStatus(item)
                                }
                              />
                            </Box>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={masterQuestionsLength || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ flexShrink: 0, borderTop: 1, borderColor: "divider" }}
          />
        </Paper>
        <DeleteAlert
          open={open}
          onClose={handleClose}
          handleDelete={() => updateMasterQuestionStatus(switchInactive)}
          header={"Deactivate Question"}
          message={"Are you sure you want to deactivate this item?"}
          rightButton={"Deactivate"}
        />
      </Box>
    </>
  );
};

export default QuestionTable;
